import {
    AllOnboardingProjectSets,
    alwaysAvailableProjectSetNames,
    hardcodedOnboardingProjectSets,
    OnboardingProjectSet,
    OnboardingProjectSets,
} from "@buildwithflux/constants";
import {
    AutoLayoutJobConfiguration,
    FallbackLegacyProPlanPrice,
    FallbackOrganizationPaidPlanPrice,
    FallbackProPlanPrice,
    FallbackStarterPlanPrice,
    isExperimentActive,
    PlanPricing,
} from "@buildwithflux/models";

import {useFluxServices} from "injection/hooks";
import {getActiveServicesContainerBadlyAsServiceLocator} from "injection/singleton";

import {FeatureFlagsState} from "./FeatureFlagsStore";
import {ContextSyncState, FeatureFlagLifecycleState, FreshnessState} from "./LaunchDarklyService";

/**
 * Select a feature flag value from the store
 *
 * Note that you can access the useFeatureFlagsStore hook from the service container yourself - this is just a
 * convenience wrapper for historical places where we've used the hook without direct access to the service container.
 *
 * @deprecated Access the store from the container: useFluxServices().useFeatureFlagsStore(selector)
 */
export function useFeatureFlags<T>(selector: (state: FeatureFlagsState) => T): T;

/**
 * Gets the entire state of the feature flags store
 *
 * This function call, where a selector is not provided, provides backwards compatibility with the Zustand
 * store API. It is not recommended to use this function.
 *
 * @deprecated You should always provide a selector, but if you need to access the entire state non-reactively, use
 * .getState() on the store: useFluxServices().useFeatureFlagsStore().getState()
 */
export function useFeatureFlags(): FeatureFlagsState;
export function useFeatureFlags<T>(selector?: (state: FeatureFlagsState) => T) {
    const services = useFluxServices();
    if (!selector) {
        return services.useFeatureFlagsStore.getState();
    }
    return services.useFeatureFlagsStore(selector);
}

/**
 * This getState() function is attached to the hook to provide backwards compatibility with the Zustand
 * store API. It is not recommended to use this function, as it bypasses the reactivity of the Zustand store. You
 * really need reactivity when accessing feature flags, because there's a long period while initializing where you'll
 * get undefined defaults for every flag, while we wait for the LaunchDarkly client to respond.
 *
 * @deprecated To access this method in a non-deprecated way, and understanding the limitations of not having
 *     reactivity, you can use the store directly from the service container: container.useFeatureFlagsStore.getState()
 */
useFeatureFlags.getState = (): FeatureFlagsState => {
    const {useFeatureFlagsStore} = getActiveServicesContainerBadlyAsServiceLocator();
    return useFeatureFlagsStore.getState();
};

export const selectLegacyProPlanPrice = (state: FeatureFlagsState): PlanPricing => {
    const value = PlanPricing.safeParse(state.segmentLegacyProPlanPrice);

    if (value.success) {
        return value.data;
    }

    return FallbackLegacyProPlanPrice;
};

export const selectProPlanPrice = (state: FeatureFlagsState): PlanPricing => {
    const value = PlanPricing.safeParse(state.segmentProPlanPrice);

    if (value.success) {
        return value.data;
    }

    return FallbackProPlanPrice;
};

export const selectStarterPlanPrice = (state: FeatureFlagsState): PlanPricing => {
    const value = PlanPricing.safeParse(state.segmentStarterPlanPrice);

    if (value.success) {
        return value.data;
    }

    return FallbackStarterPlanPrice;
};

export const selectOrganizationPaidPlanPrice = (state: FeatureFlagsState): PlanPricing => {
    const value = PlanPricing.safeParse(state.segmentOrganizationPaidPlanPrice);

    if (value.success) {
        return value.data;
    }

    return FallbackOrganizationPaidPlanPrice;
};

// Trivial function, used for fast toggling during dev
export const selectShowNewProjectDialog = (state: FeatureFlagsState): boolean => {
    return state.segmentNewProjectDialog;
};

export const selectAllOnboardingProjectSets = (state: FeatureFlagsState): OnboardingProjectSets => {
    const allSets = AllOnboardingProjectSets.safeParse(state.onboardingProjectSetsList);
    if (!allSets.success) {
        return hardcodedOnboardingProjectSets;
    } else {
        return allSets.data;
    }
};

export const selectOnboardingProjectSet = (state: FeatureFlagsState): OnboardingProjectSet => {
    const sets = selectAllOnboardingProjectSets(state);

    return sets[state.segmentOnboardingProjectSetName || alwaysAvailableProjectSetNames._default] || sets._default;
};

export const selectAutoLayoutJobConfiguration = (state: FeatureFlagsState) => {
    return AutoLayoutJobConfiguration.parse(state.pcbAutoLayoutJobConfiguration ?? {});
};

export const selectAutoLayoutVersion = (state: FeatureFlagsState) => {
    return state.pcbAutoLayoutJobConfiguration?.version ?? "v1";
};

export function usePcbAutoLayoutEnabled(): boolean {
    // pcbAutoLayout feature flag is now always enabled (was always true)
    return true;
}

export function useFreePlanMigrationShowExpiryMessaging(): boolean {
    return useFluxServices().useFeatureFlagsStore((state) => state.freePlanMigrationShowExpiryMessaging ?? false);
}

export function useFreePlanMigrationShowAnnouncementDialog(): boolean {
    return isExperimentActive(
        useFluxServices().useFeatureFlagsStore((state) => state.segmentFreePlanMigrationShowAnnouncementDialog),
    );
}

export function useCancelPlanDialog(): boolean {
    return isExperimentActive(useFluxServices().useFeatureFlagsStore((state) => state.segmentCancelPlanDialog));
}

/**
 * Returns the current feature flag lifecycle state.
 * Use this to check if flags are ready before performing critical actions.
 *
 * @returns The current lifecycle state with contextSync and freshness
 */
export function useFeatureFlagLifecycleState(): FeatureFlagLifecycleState {
    const contextSync = useFluxServices().useFeatureFlagsStore((state) => state.contextSync);
    const freshness = useFluxServices().useFeatureFlagsStore((state) => state.freshness);
    return {contextSync, freshness};
}

/**
 * Returns whether feature flags are ready (freshness !== "invalid").
 * Use this to gate UI or actions that depend on having valid flag values.
 *
 * @returns true if flags are at least cached for the correct user context
 */
export function useFeatureFlagsReady(): boolean {
    return useFluxServices().useFeatureFlagsStore((state) => state.freshness !== "invalid");
}

/**
 * Returns whether feature flags are fresh from the server.
 * Use this for critical actions that require the most up-to-date flag values.
 *
 * @returns true if flags have been confirmed from LaunchDarkly server
 */
export function useFeatureFlagsFresh(): boolean {
    return useFluxServices().useFeatureFlagsStore((state) => state.freshness === "fresh");
}

/**
 * Selectors for feature flag lifecycle state.
 * Use these with useFeatureFlagsStore for more granular control.
 */
export const selectFeatureFlagContextSync = (state: FeatureFlagsState): ContextSyncState => state.contextSync;
export const selectFeatureFlagFreshness = (state: FeatureFlagsState): FreshnessState => state.freshness;
export const selectFeatureFlagsReady = (state: FeatureFlagsState): boolean => state.freshness !== "invalid";
export const selectFeatureFlagsFresh = (state: FeatureFlagsState): boolean => state.freshness === "fresh";
